<template>
    <Card>
        <div class="card-body">
            <AppButton
                color="indigo"
                class="mr-2"
                :disabled="isRecalculating"
                @click="recalculatePrices"
            >
                <i :class="['icon-sync mr-2', isRecalculating ? 'spinner' : '']"></i>
                <span>{{ translate('buttons.recalculate_prices') }}</span>
            </AppButton>
            <router-link class="btn btn-link" :to="{ name: 'PriceTemplateCreatePage' }">
                <i class="icon-plus3 mr-2"></i>
                {{ $t('buttons.create') }}
            </router-link>
        </div>
        <FetchTable
            :handle="tableHandle"
            :url="priceTemplatesUrl"
            v-model:orders="table.orders"
            :columns="table.columns"
        >
            <template #card-body-actions>
                <div class="d-flex flex-wrap" style="gap: .5rem;">
                    <router-link class="btn btn-link" :to="{ name: 'PriceTemplateCreatePage' }">
                        <i class="icon-plus3 mr-2"></i>
                        {{ $t('buttons.create') }}
                    </router-link>
                    <AppButton
                        color="indigo"
                        :disabled="isRecalculating"
                        @click="recalculatePrices"
                    >
                        <i :class="['icon-sync mr-2', isRecalculating ? 'spinner' : '']"></i>
                        <span>{{ translate('buttons.recalculate_prices') }}</span>
                    </AppButton>
                </div>
            </template>
            <template #td-id="{ row }">
                <router-link :to="{ name: 'PriceTemplateEditPage', params: { priceTemplateUuid: row.uuid } }">
                    {{ row.id }}
                </router-link>
            </template>
            <template #td-formula="{ row }">
                <span class="formula-cell" v-html="renderFormula(row.formula)"></span>
            </template>
            <template #td-show_in_order="{ row }">
                {{ row.show_in_order ? $t('labels.yes') : $t('labels.no') }}
            </template>
        </FetchTable>
    </Card>
</template>
<script>
import { privateChannel } from '../../../services/echoService';

import FetchTable from "../../../../components/new/table/FetchTable.vue";
import { createTableHandle } from "../../../../components/new/table/tableHandle";
import AppButton from "../../../../components/new/button/AppButton.vue";
import Card from "../../../../components/new/CardComponent.vue";

import Project from "../../../../models/Project";
import { httpClient } from "../../../../services/HttpClient";
import { CustomFieldApi } from "../../../api/CustomFieldApi";

const RECALCULATE_PRICES_TASK = 'recalculate:price_template-values';

export default {
    name: "PriceTemplatesIndexPage",
    components: {
        FetchTable, AppButton, Card
    },
    data() {
        return {
            tableHandle: createTableHandle(),
            project: new Project(),
            customFields: [],
            dropshippers: [],
            isUnmounted: false,
            table: {
                orders: [{ column: 'id', dir: 'desc' }],
                columns: [
                    { key: "id", name: translate('labels.id'), width: "150px", type: "string", order: true },
                    { key: "name", name: translate('labels.name'), width: "250px", type: "string", order: true },
                    { key: "formula", name: translate('labels.price_template_page.formula'), width: "500px", type: "string", order: false },
                    { key: "rounding_step", name: translate('labels.rounding_to'), width: "250px", type: "string", order: false },
                    { key: "show_in_order", name: translate('labels.price_template_page.show_in_order'), width: "250px", type: "string", order: false },
                ]
            }
        }
    },
    computed: {
        priceTemplatesUrl() {
            return `/projects/${this.$route.params.projectUuid}/pim/price-templates`;
        },
        isRecalculating() {
            return this.project.hasBackgroundTask(RECALCULATE_PRICES_TASK);
        },
        formulaVariables() {
            let variables = [
                { code: '{$categoryMarkup}', name: translate('labels.category_type_markup') },
                { code: '{$cost}', name: translate('labels.cost') },
            ];

            this.customFields.map((customField) => variables.push({
                code: `{$customField[${customField.id}]}`,
                name: customField.title
            }))

            this.dropshippers.map((dropshipper) => variables.push({
                code: `{$dropshipper[${dropshipper.uuid}]}`,
                name: translate('labels.purchase_price', { seller: dropshipper.name })
            }))

            return variables;
        },
        formulaVariablesMap() {
            return this.formulaVariables.reduce((map, item) => {
                map[item.code.replace(/{|}/g, '')] = item.name;

                return map;
            }, {});
        }
    },
    methods: {
        renderFormula(formula) {
            if (!formula) {
                return '';
            }

            return formula.split(/({[^{}]+})/).map((part) => {
                const match = part.match(/{([^{}]+)}/);

                if (match && this.formulaVariablesMap[match[1]]) {
                    return `<span class="badge badge-light">${this.formulaVariablesMap[match[1]]}</span>`;
                }

                return part;
            }).join('');
        },
        async fetchProject() {
            let response = await httpClient.get(`/projects/${this.$route.params.projectUuid}`);

            this.project = new Project(response.data.data);
        },
        async fetchCustomFields() {
            this.customFields = await CustomFieldApi.findAll(this.$route.params.projectUuid, {
                models: ['product-item'],
                types: ['number'],
                limit: 0
            });
        },
        async fetchDropshippers() {
            let response = await httpClient.get('/dropshippers', {
                customer_project_uuids: [this.project.uuid],
                extra: 1,
                limit: 0
            });

            this.dropshippers = response.data.data;
        },
        async recalculatePrices() {
            await httpClient.patch(`/projects/${this.project.uuid}`, {
                background_tasks: [RECALCULATE_PRICES_TASK]
            });

            this.project.addBackgroundTask(RECALCULATE_PRICES_TASK);
        },
        listenProjectUpdates() {
            this.channel = privateChannel(`projects.${this.project.id}`)
                .listen('\\App\\Events\\Project\\ProjectUpdatedEvent', this.onProjectUpdated)
        },
        async onProjectUpdated() {
            let wasRecalculating = this.isRecalculating;

            await this.fetchProject();

            if (wasRecalculating && !this.isRecalculating) {
                this.tableHandle.fetchRows();
            }
        }
    },
    async mounted() {
        this.fetchCustomFields();

        await this.fetchProject();

        if (this.isUnmounted) {
            return;
        }

        await this.tableHandle.fetchRows();

        this.fetchDropshippers();

        this.listenProjectUpdates();
    },
    beforeUnmount() {
        this.isUnmounted = true;

        if (this.channel) {
            this.channel.stopListening('\\App\\Events\\Project\\ProjectUpdatedEvent', this.onProjectUpdated);
            this.channel = null;
        }
    }
}
</script>
<style scoped>
.formula-cell {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
}
.formula-cell :deep(.badge) {
    margin: 0 1px;
}
</style>
